import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
  useId,
} from "react";
import classNames from "classnames/bind";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { ArrowLeftOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ArrowLeftOutlined";
import { ArrowRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ArrowRightOutlined";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { GalleryImage } from "./GalleryImage";
import { GalleryVideo } from "./GalleryVideo";
import { ZoomModal } from "@/components/Galleries/components/ZoomModal/ZoomModal";
import { ZoomModal as PremiumZoomModal } from "./ZoomModal/ZoomModal";
import { SocialProofMessagesProperties } from "@/components/Galleries/utils/utils";
import { SocialProofMessages } from "@/components/Galleries/components/SocialProofMessages/SocialProofMessages";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import {
  GalleryObject,
  GalleryImageObject,
  GalleryVideoObject,
  ProductImages,
} from "@/components/Galleries/utils/utils";
import { Video } from "@/types/prodcatBaseTypes";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import { ErrorBoundary } from "@/utils/error/ErrorBoundary";
import styles from "./Gallery.module.scss";

const cx = classNames.bind(styles);

type ScrollState = {
  selectedIndex: number;
  scrollProgress: number;
  thumbRatio: number;
  isAtScrollEnd: boolean;
  isCarouselMode: boolean;
};

const initialScrollState: ScrollState = {
  selectedIndex: 0,
  scrollProgress: 0,
  thumbRatio: 1,
  isAtScrollEnd: false,
  isCarouselMode: false,
};

function scrollReducer(_state: ScrollState, action: ScrollState): ScrollState {
  return action;
}

// Detects a colour-variant switch (same-length media lists with different content)
const generateMediaItemsKey = (mediaItems: GalleryObject[]) =>
  mediaItems.map((item) => item.thumbnailUrl).join("|");

type GalleryUIProps = {
  title: string;
  images: ProductImages;
  videos: Video[];
  sizes?: string;
  hasSocialProofMessagesExperience?: boolean;
  socialProofMessagesProperties?: SocialProofMessagesProperties;
  useNewSocialProofMessagesPosition?: boolean;
};

function buildMediaItems(
  title: string,
  images: ProductImages,
  videos: Video[],
): GalleryObject[] {
  const imageItems: GalleryImageObject[] = [
    images.primary,
    ...images.alternatives,
  ].map((url) => ({
    type: "image",
    title,
    imageUrl: getBaseUrlAndQuery(url).baseUrl || "",
    thumbnailUrl: formatAmplienceImageURL(`${url}?wid=120&hei=160`),
  }));

  const videoItems: GalleryVideoObject[] = videos.map((video) => {
    const videoThumbnail = video.sources.find(
      (source) => source.key === "thumbnail",
    );
    const { baseUrl = "" } = getBaseUrlAndQuery(videoThumbnail?.url || "");

    return {
      type: "video",
      title,
      imageUrl: baseUrl,
      thumbnailUrl: `${baseUrl}?wid=120&hei=160`,
      videoUrl: video.sources.find((source) => source.key === "stream")?.url,
      posterUrl: video.sources.find((source) => source.key === "poster")?.url,
    };
  });

  // With 3+ images, lead with the first 3 images, then the first video, then the rest
  const [firstVideo, ...remainingVideos] = videoItems;
  if (imageItems.length >= 3 && firstVideo) {
    return [
      ...imageItems.slice(0, 3),
      firstVideo,
      ...imageItems.slice(3),
      ...remainingVideos,
    ];
  }

  return [...imageItems, ...videoItems];
}

export const GalleryUI = ({
  title,
  images,
  videos,
  sizes = "100vw",
  hasSocialProofMessagesExperience = false,
  socialProofMessagesProperties,
}: GalleryUIProps) => {
  const mediaItems = useMemo(
    () => buildMediaItems(title, images, videos),
    [title, images, videos],
  );
  const headingId = useId();
  const [scrollState, dispatchScroll] = useReducer(
    scrollReducer,
    initialScrollState,
  );
  const {
    selectedIndex,
    scrollProgress,
    thumbRatio,
    isAtScrollEnd,
    isCarouselMode,
  } = scrollState;
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const [modalSelectedIndex, setModalSelectedIndex] = useState(0);
  const [announcedIndex, setAnnouncedIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const announceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mediaItemsKeyRef = useRef(generateMediaItemsKey(mediaItems));

  const { flags } = useStandardProps();
  const premiumZoomModal = flags.premiumZoomModal.value === true;
  const hasDeferredMonetateQEvents = !!flags.hasDeferredMonetateQEvents.value;

  const scrollToIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= mediaItems.length || !carouselRef.current) {
        return;
      }
      const target = carouselRef.current.children[index];
      if (target instanceof HTMLElement) {
        carouselRef.current.scrollTo({
          left: target.offsetLeft,
          behavior: "smooth",
        });
      }
    },
    [mediaItems.length],
  );

  const updateScrollbar = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const carouselMode = maxScroll > 0;

    // Derive selectedIndex from scroll position
    const children = [...el.children];
    let closestIndex = 0;
    let closestDistance = Infinity;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child instanceof HTMLElement) {
        const distance = Math.abs(child.offsetLeft - el.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }
    }

    dispatchScroll({
      isCarouselMode: carouselMode,
      scrollProgress: carouselMode ? el.scrollLeft / maxScroll : 0,
      thumbRatio: carouselMode ? el.clientWidth / el.scrollWidth : 1,
      isAtScrollEnd:
        Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth,
      selectedIndex: closestIndex,
    });
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    let rafId = 0;

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScrollbar);
    };

    updateScrollbar();
    el.addEventListener("scroll", onScroll, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
    };
  }, [mediaItems.length, updateScrollbar]);

  // Jump back to the first slide when a new colour variant's media replaces the current set
  useEffect(() => {
    const key = generateMediaItemsKey(mediaItems);
    if (key === mediaItemsKeyRef.current) return;
    mediaItemsKeyRef.current = key;
    carouselRef.current?.scrollTo({ left: 0 });
    updateScrollbar();
  }, [mediaItems, updateScrollbar]);

  // Debounce live region announcements to avoid excessive screen reader output
  useEffect(() => {
    if (announceTimerRef.current) {
      clearTimeout(announceTimerRef.current);
    }
    announceTimerRef.current = setTimeout(() => {
      setAnnouncedIndex(selectedIndex);
    }, 500);
    return () => {
      if (announceTimerRef.current) {
        clearTimeout(announceTimerRef.current);
      }
    };
  }, [selectedIndex]);

  const handlePrevClick = () => {
    dispatchGalleryMonetateQEvent(
      "LeftArrow",
      "generic",
      hasDeferredMonetateQEvents,
    );
    scrollToIndex(selectedIndex - 1);
  };

  const handleNextClick = () => {
    dispatchGalleryMonetateQEvent(
      "RightArrow",
      "generic",
      hasDeferredMonetateQEvents,
    );
    scrollToIndex(selectedIndex + 1);
  };

  const handleSlideKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const nextIndex = event.key === "ArrowLeft" ? index - 1 : index + 1;
      scrollToIndex(nextIndex);
      // Move focus to the primary focusable element in the next slide
      const el = carouselRef.current;
      if (el) {
        const nextSlide = el.children[nextIndex];
        if (nextSlide instanceof HTMLElement) {
          const focusable = nextSlide.querySelector<HTMLElement>(
            'button, [tabindex="0"]',
          );
          focusable?.focus();
        }
      }
    },
    [scrollToIndex],
  );

  const openZoomModal = useCallback((index: number, image?: boolean) => {
    if (image) {
      dispatchGalleryMonetateQEvent("ZoomIcon", undefined, undefined);
    }
    setModalSelectedIndex(index);
    setIsZoomModalOpen(true);
  }, []);

  const closeZoomModal = () => {
    setIsZoomModalOpen(false);
  };

  const handleVideoPlayStateChange = useCallback(
    (index: number, playing: boolean) => {
      setActiveVideoIndex((current) => {
        if (playing) return index;
        return current === index ? null : current;
      });
    },
    [],
  );

  return (
    <section
      className={styles.gallery}
      data-testid="gallery"
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className={styles.visuallyHidden}>
        Product gallery
      </h2>
      {hasSocialProofMessagesExperience && socialProofMessagesProperties && (
        <div className={styles.socialProofMessagesWrapper}>
          <ErrorBoundary componentName="SocialProofMessages">
            <SocialProofMessages
              {...socialProofMessagesProperties}
              className="gridGalleryInstance"
            />
          </ErrorBoundary>
        </div>
      )}
      <div
        className={cx(
          styles.carouselWrapper,
          {
            oneItem: mediaItems.length === 1,
          },
          {
            twoItems: mediaItems.length === 2,
          },
        )}
      >
        <div
          className={styles.mediaContainer}
          ref={carouselRef}
          {...(isCarouselMode && {
            role: "region",
            "aria-label": "Product media",
            "aria-roledescription": "carousel",
          })}
        >
          {mediaItems.map((item, index) => (
            <div
              key={`${item.imageUrl}-${index}`}
              className={styles.mediaItem}
              {...(isCarouselMode && {
                role: "group",
                "aria-roledescription": "slide",
                "aria-label": `${index + 1} of ${mediaItems.length}`,
              })}
            >
              {item.type === "video" ? (
                <GalleryVideo
                  video={item}
                  index={index}
                  total={mediaItems.length}
                  onSelect={isCarouselMode ? openZoomModal : undefined}
                  onKeyDown={
                    isCarouselMode
                      ? (e: React.KeyboardEvent) => handleSlideKeyDown(e, index)
                      : undefined
                  }
                  isActive={activeVideoIndex === index}
                  onPlayStateChange={handleVideoPlayStateChange}
                />
              ) : (
                <GalleryImage
                  mediaItem={item}
                  index={index}
                  total={mediaItems.length}
                  sizes={sizes}
                  onSelect={openZoomModal}
                  onKeyDown={
                    isCarouselMode
                      ? (e: React.KeyboardEvent) => handleSlideKeyDown(e, index)
                      : undefined
                  }
                />
              )}
            </div>
          ))}
        </div>

        {mediaItems.length > 1 && (
          <>
            {/* Scrollbar indicator - carousel only */}
            <div className={styles.scrollbar} aria-hidden="true">
              <div
                className={styles.scrollbarThumb}
                style={{
                  width: `${thumbRatio * 100}%`,
                  transform: `translateX(${scrollProgress * (1 / thumbRatio - 1) * 100}%)`,
                }}
              />
            </div>

            {/* Navigation buttons - carousel only */}
            <div className={styles.carouselButtons}>
              <IconButton
                icon={ArrowLeftOutlined}
                background="clear"
                onClick={handlePrevClick}
                disabled={selectedIndex < 1}
                aria-label={`Previous: Show slide ${selectedIndex > 0 ? selectedIndex : 1} of ${mediaItems.length}`}
                data-testid="gallery:navigation:previous"
              />
              <IconButton
                icon={ArrowRightOutlined}
                background="clear"
                onClick={handleNextClick}
                disabled={isAtScrollEnd}
                aria-label={`Next: Show slide ${selectedIndex < mediaItems.length - 1 ? selectedIndex + 2 : mediaItems.length} of ${mediaItems.length}`}
                data-testid="gallery:navigation:next"
              />
            </div>
          </>
        )}
      </div>

      {/* Live region for screen readers */}
      <div
        className={styles.visuallyHidden}
        aria-live="polite"
        aria-atomic="true"
      >
        {`Viewing ${mediaItems[announcedIndex]?.type === "video" ? "video" : "image"} ${announcedIndex + 1} of ${mediaItems.length}`}
      </div>

      {/* Zoom modal */}
      {premiumZoomModal ? (
        <PremiumZoomModal
          closeZoomModal={closeZoomModal}
          mediaItems={mediaItems}
          selectedIndex={modalSelectedIndex}
          setSelectedIndex={setModalSelectedIndex}
          isZoomModalOpen={isZoomModalOpen}
        />
      ) : (
        <ZoomModal
          closeZoomModal={closeZoomModal}
          mediaItems={mediaItems}
          selectedIndex={modalSelectedIndex}
          setSelectedIndex={setModalSelectedIndex}
          isZoomModalOpen={isZoomModalOpen}
          videoAutoplayOnLoad={mediaItems[modalSelectedIndex]?.type === "video"}
        />
      )}
    </section>
  );
};
